import * as React from "react";

import { ACountry } from "../../model";
import { useLocalizationProvider } from "../../providers/localization-provider";
import { replaceAll } from "../../utils";

import {
  FormLogicEventBehaviour,
  FormLogicEventBehaviour_OnChange,
  GenericTextFieldFormLogic,
  GenericTextFieldFormLogicProps,
} from "./generic";

export interface MobileFormLogic2023Props {
  formRef: GenericTextFieldFormLogicProps["ref"];
  required: GenericTextFieldFormLogicProps["required"];
  countryCode?: string | null | undefined;
}
export const MobileFormLogic2023: React.FunctionComponent<
  MobileFormLogic2023Props
> = (props) => {
  const { countryCode } = useLocalizationProvider();
  const effectiveCountryCode = React.useMemo(() => {
    const overrideCountryCode = String(props.countryCode || "")
      .trim()
      .toLowerCase();
    if (overrideCountryCode.length > 0) {
      return overrideCountryCode;
    }

    return String(countryCode || "")
      .trim()
      .toLowerCase();
  }, [props.countryCode, countryCode]);

  const customErrorValidation = React.useCallback(
    (value: any) => {
      if (effectiveCountryCode === ACountry.Australia) {
        const mobileValue = String(value || "").trim();
        if (!isValidAustralianMobileNumber(mobileValue)) {
          return "Please enter a valid mobile number.";
        }
        return null;
      }

      const valueWithoutPlus = replaceAll(value || "", "\\+", "");
      const isAnythingOtherThanDigits = /[^\d]/.test(valueWithoutPlus);
      if (isAnythingOtherThanDigits) {
        return "Please enter a valid number in this field.";
      }
      return null;
    },
    [effectiveCountryCode]
  );

  return (
    <GenericTextFieldFormLogic
      ref={props.formRef}
      behaviour={{
        onFocus: FormLogicEventBehaviour.nothing,
        onChange: FormLogicEventBehaviour_OnChange.mutateWithValidation,
        onBlur: FormLogicEventBehaviour.validate,
        onSubmit: FormLogicEventBehaviour.validate,
      }}
      customLength={customLength}
      customErrorValidation={customErrorValidation}
      required={props.required}
    />
  );
};

const customLength = (value: any) => {
  const valueWithoutPlus = replaceAll(value || "", "\\+", "");
  let newValue = valueWithoutPlus.substring(0, maxMobileNumberLength);
  if (((value as string) || "").includes("+")) {
    newValue = `+${newValue}`;
  }
  return {
    value: newValue,
  };
};

const isValidAustralianMobileNumber = (value: string) =>
  /^(?:04\d{8}|05\d{8}|\+614\d{8}|\+615\d{8}|614\d{8}|615\d{8})$/.test(
    value || ""
  );

export const maxMobileNumberLength = 12;
